import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { clientsService, commandesService } from "@/api/services";
import { DataTable, type Column } from "@/components/common/DataTable";
import { EmptyState } from "@/components/common/EmptyState";
import { ErrorState } from "@/components/common/ErrorState";
import { Filters } from "@/components/common/Filters";
import { LoadingState } from "@/components/common/LoadingState";
import { PageHeader } from "@/components/common/PageHeader";
import { StatusBadge } from "@/components/common/StatusBadge";
import { useI18n } from "@/i18n/I18nProvider";
import {
  COMMANDE_STATUTS,
  type Client,
  type Commande,
  type CommandeStatut,
} from "@/types/domain";

type StatutFilter = "ALL" | CommandeStatut;

const isCommandeStatut = (value: string | null): value is CommandeStatut =>
  value !== null && COMMANDE_STATUTS.includes(value as CommandeStatut);

export default function CommandesPage() {
  const { t, lang } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();
  const statutParam = searchParams.get("statut");
  const statut: StatutFilter = isCommandeStatut(statutParam)
    ? statutParam
    : "ALL";

  const [clientId, setClientId] = useState("ALL");
  const [clients, setClients] = useState<Client[]>([]);
  const [clientFilterError, setClientFilterError] = useState(false);
  const [commandes, setCommandes] = useState<Commande[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const locale = lang === "fr" ? "fr-CA" : "en-CA";

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat(locale, {
      style: "currency",
      currency: "CAD",
      maximumFractionDigits: 2,
    }).format(value);

  const formatDate = (value: string) => {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "—" : date.toLocaleDateString(locale);
  };

  const loadCommandes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await commandesService.list({
        ...(statut !== "ALL" ? { statut } : {}),
        ...(clientId !== "ALL" ? { client_id: clientId } : {}),
      });
      setCommandes(data);
    } catch (err) {
      console.error("Erreur chargement commandes :", err);
      setError("Impossible de charger les commandes.");
    } finally {
      setLoading(false);
    }
  }, [clientId, statut]);

  useEffect(() => {
    loadCommandes();
  }, [loadCommandes]);

  useEffect(() => {
    const loadClients = async () => {
      try {
        setClientFilterError(false);
        const data = await clientsService.list();
        setClients(data);
      } catch (err) {
        console.error("Erreur chargement clients pour le filtre :", err);
        setClientFilterError(true);
      }
    };

    loadClients();
  }, []);

  const handleStatutChange = (value: string) => {
    const nextParams = new URLSearchParams(searchParams);

    if (value === "ALL") {
      nextParams.delete("statut");
    } else if (isCommandeStatut(value)) {
      nextParams.set("statut", value);
    }

    setSearchParams(nextParams, { replace: true });
  };

  const columns: Column<Commande>[] = [
    {
      key: "numero_commande",
      header: "N° commande",
      mono: true,
      render: (commande) => (
        <Link
          to={`/commandes/${commande.id}`}
          data-command-id={commande.id}
          className="ops-focus rounded-sm font-semibold text-foreground hover:underline"
        >
          {commande.numero_commande}
        </Link>
      ),
    },
    {
      key: "client",
      header: "Client",
      render: (commande) => commande.client.nom || "—",
    },
    {
      key: "numero_po_client",
      header: "N° PO client",
      mono: true,
      render: (commande) => commande.numero_po_client || "—",
    },
    {
      key: "date_reception",
      header: "Date de réception",
      mono: true,
      render: (commande) => formatDate(commande.date_reception),
    },
    {
      key: "statut",
      header: "Statut",
      render: (commande) => <StatusBadge status={commande.statut} />,
    },
    {
      key: "valeur_commande",
      header: "Valeur",
      align: "right",
      mono: true,
      render: (commande) =>
        commande.valeur_commande == null
          ? "—"
          : formatCurrency(commande.valeur_commande),
    },
    {
      key: "numero_commande_sage",
      header: "Commande Sage",
      mono: true,
      render: (commande) => commande.numero_commande_sage || "—",
    },
    {
      key: "numero_facture_sage",
      header: "Facture Sage",
      mono: true,
      render: (commande) => commande.numero_facture_sage || "—",
    },
    {
      key: "facture_creee",
      header: "Facture créée",
      render: (commande) => (
        <StatusBadge
          status={commande.facture_creee ? "Oui" : "Non"}
          tone={commande.facture_creee ? "success" : "neutral"}
        />
      ),
    },
    {
      key: "facture_envoyee",
      header: "Facture envoyée",
      render: (commande) => (
        <StatusBadge
          status={commande.facture_envoyee ? "Oui" : "Non"}
          tone={commande.facture_envoyee ? "success" : "neutral"}
        />
      ),
    },
  ];

  const hasActiveFilter = statut !== "ALL" || clientId !== "ALL";

  return (
    <div className="space-y-6" data-testid="commandes-page">
      <PageHeader
        title={t.modules.commandes.title}
        subtitle={t.modules.commandes.subtitle}
        breadcrumb={[t.appSuffix, t.modules.commandes.title]}
        actions={
          <Link
            to="/commandes/nouvelle"
            className="ops-focus inline-flex rounded-sm bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Nouvelle commande
          </Link>
        }
      />

      <section className="ops-panel" data-testid="commandes-list-panel">
        <div className="flex flex-col gap-3 border-b border-border px-4 py-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-sm font-semibold tracking-tight">
              Liste des commandes
            </h2>
            <p className="mt-0.5 font-mono text-[11px] text-muted-foreground">
              Source : GET /commandes
            </p>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <Filters
              label="Statut"
              value={statut}
              onChange={handleStatutChange}
              testId="commandes-statut-filter"
              options={[
                { value: "ALL", label: "Tous les statuts" },
                ...COMMANDE_STATUTS.map((value) => ({
                  value,
                  label: value.replace(/_/g, " "),
                })),
              ]}
            />

            {clients.length > 0 && (
              <Filters
                label="Client"
                value={clientId}
                onChange={setClientId}
                testId="commandes-client-filter"
                options={[
                  { value: "ALL", label: "Tous les clients" },
                  ...clients.map((client) => ({
                    value: client.id,
                    label: client.nom_legal,
                  })),
                ]}
              />
            )}
          </div>
        </div>

        {clientFilterError && (
          <p className="border-b border-border px-4 py-2 text-xs text-muted-foreground">
            Le filtre client est temporairement indisponible.
          </p>
        )}

        {loading ? (
          <LoadingState label="Chargement des commandes..." rows={5} />
        ) : error ? (
          <ErrorState
            title="Chargement impossible"
            description={error}
            retryLabel="Réessayer"
            onRetry={loadCommandes}
            className="m-4"
          />
        ) : commandes.length === 0 ? (
          <EmptyState
            title={
              hasActiveFilter
                ? "Aucune commande ne correspond aux filtres."
                : "Aucune commande disponible."
            }
            description={
              hasActiveFilter
                ? "Modifiez les filtres pour afficher d'autres commandes."
                : "Les commandes apparaîtront ici lorsqu'elles seront disponibles."
            }
          />
        ) : (
          <DataTable
            columns={columns}
            rows={commandes}
            rowKey={(commande) => commande.id}
            testId="commandes-table"
          />
        )}
      </section>
    </div>
  );
}
